import { useEffect, useMemo } from 'react';
import { hasGlobalTerritoriesApplied } from '@helpers/territories';
import { actionTypes } from '../reducer';
import FilterTypes from '../../../../constants/FilterTypes.ts';

const isFilterValuePopulated = (value) => {
  if (Array.isArray(value)) return value.length > 0;
  return typeof value === 'string' && value !== '';
};

export default function useFilters({
  filters,
  dispatch,
  resolutionStatusesForSelect,
  deficiencyStatusesForSelect,
  salesPeopleForSelect,
  assetsForSelect,
  screen
}) {
  const globalTerritoriesApplied = hasGlobalTerritoriesApplied();
  const hasPrimaryTerritoryScope = globalTerritoriesApplied || isFilterValuePopulated(filters.values.territory);

  useEffect(() => {
    if (!hasPrimaryTerritoryScope && isFilterValuePopulated(filters.values.additionalTerritory)) {
      dispatch({ type: actionTypes.FILTER_CHANGED, value: '', field: 'additionalTerritory' });
    }
  }, [hasPrimaryTerritoryScope, filters.values.additionalTerritory, dispatch]);
  const onToggleFilter = (fieldName) => {
    return () => {
      dispatch({ type: actionTypes.FILTER_TOGGLED, field: fieldName });
    };
  };

  const onChangeDateFilter = (fieldName) => {
    return (dateFieldName, value) => {
      dispatch({
        type: actionTypes.DATE_RANGE_FILTER_CHANGED,
        field: fieldName,
        dateField: dateFieldName,
        value
      });
    };
  };

  const onChangeSearchFilter = (fieldName) => {
    return (e) => {
      dispatch({ type: actionTypes.FILTER_CHANGED, value: e.sender.value(), field: fieldName });
    };
  };

  const handleMultiSelectFilterChange = (fieldName) => {
    return (value) => {
      dispatch({ type: actionTypes.FILTER_CHANGED, value, field: fieldName });
    };
  };

  const onChangeReactFilter = (fieldName) => {
    return (value) => {
      dispatch({ type: actionTypes.FILTER_CHANGED, value, field: fieldName });
    };
  };

  const indexFilterList = () => {
    const list = [
      {
        field: 'resolved',
        locale: 'resolved',
        type: 'booleanSelect',
        active: filters.active.resolved,
        value: filters.values.resolved,
        onChange: onChangeReactFilter('resolved'),
        onToggle: onToggleFilter('resolved')
      },
      {
        field: 'dateOpened',
        locale: 'date_opened',
        type: 'dateRangePicker',
        active: filters.active.dateOpened,
        values: filters.values.dateOpened,
        onChange: onChangeDateFilter('dateOpened'),
        onToggle: onToggleFilter('dateOpened')
      },
      {
        field: 'lastReportedOn',
        locale: 'last_reported_on',
        type: 'dateRangePicker',
        active: filters.active.lastReportedOn,
        values: filters.values.lastReportedOn,
        onChange: onChangeDateFilter('lastReportedOn'),
        onToggle: onToggleFilter('lastReportedOn')
      },
      {
        field: 'building',
        locale: 'building',
        type: FilterTypes.GenericMultiSelect,
        active: filters.active.building,
        value: filters.values.building,
        onChange: handleMultiSelectFilterChange('building'),
        onToggle: onToggleFilter('building')
      },
      {
        field: 'account',
        locale: 'account',
        type: FilterTypes.GenericMultiSelect,
        active: filters.active.account,
        value: filters.values.account,
        onChange: handleMultiSelectFilterChange('account'),
        onToggle: onToggleFilter('account')
      },
      {
        field: 'assets',
        locale: 'asset',
        type: FilterTypes.TreeMultiSelect,
        active: filters.active.assets,
        value: filters.values.assets,
        optionsForSelect: assetsForSelect,
        onChange: handleMultiSelectFilterChange('assets'),
        onToggle: onToggleFilter('assets')
      },
      {
        field: 'resolutionStatus',
        locale: 'resolution_status',
        type: FilterTypes.GenericMultiSelect,
        active: filters.active.resolutionStatus,
        optionsForSelect: resolutionStatusesForSelect.map((opt) => ({ title: opt.label, value: opt.value })),
        value: filters.values.resolutionStatus,
        onChange: handleMultiSelectFilterChange('resolutionStatus'),
        onToggle: onToggleFilter('resolutionStatus')
      },
      {
        field: 'deficiencyStatus',
        locale: 'deficiency_status',
        type: 'commonSelect',
        active: filters.active.deficiencyStatus,
        optionsForSelect: deficiencyStatusesForSelect,
        value: filters.values.deficiencyStatus,
        onChange: onChangeReactFilter('deficiencyStatus'),
        onToggle: onToggleFilter('deficiencyStatus')
      },
      {
        field: 'technician',
        backendField: 'technician',
        locale: 'reported_by',
        type: 'multiSelectWithAvatar',
        active: filters.active.technician,
        value: filters.values.technician,
        withUnassigned: true,
        onlyFullTech: true,
        itemHeight: 48,
        onChange: onChangeSearchFilter('technician'),
        onToggle: onToggleFilter('technician')
      },
      {
        field: 'salesPerson',
        locale: 'sales_person',
        type: FilterTypes.GenericMultiSelect,
        active: filters.active.salesPerson,
        optionsForSelect: salesPeopleForSelect.map((opt) => ({
          title: opt.subtitle || 'N/A',
          value: opt.value,
          subtitle: opt.label
        })),
        value: filters.values.salesPerson,
        onChange: handleMultiSelectFilterChange('salesPerson'),
        onToggle: onToggleFilter('salesPerson')
      },
      {
        field: 'inspectionId',
        locale: 'inspection_id',
        type: 'textBox',
        active: filters.active.inspectionId,
        value: filters.values.inspectionId,
        onChange: onChangeReactFilter('inspectionId'),
        onToggle: onToggleFilter('inspectionId')
      },
      {
        field: 'deficiencyId',
        locale: 'deficiency_id',
        type: 'textBox',
        active: filters.active.deficiencyId,
        value: filters.values.deficiencyId,
        onChange: onChangeReactFilter('deficiencyId'),
        onToggle: onToggleFilter('deficiencyId')
      }
    ];

    if (!globalTerritoriesApplied) {
      list.push({
        field: 'territory',
        locale: 'territory',
        type: FilterTypes.GenericMultiSelect,
        active: filters.active.territory,
        value: filters.values.territory,
        withUnassigned: true,
        onChange: handleMultiSelectFilterChange('territory'),
        onToggle: onToggleFilter('territory')
      });
    }

    list.push({
      field: 'additionalTerritory',
      backendField: 'additional_territory',
      locale: 'additional_territory',
      type: hasPrimaryTerritoryScope ? FilterTypes.GenericMultiSelect : FilterTypes.Hidden,
      active: filters.active.additionalTerritory,
      value: filters.values.additionalTerritory,
      onChange: handleMultiSelectFilterChange('additionalTerritory'),
      onToggle: onToggleFilter('additionalTerritory')
    });

    return list;
  };

  const inspectionFilterList = () => [
    {
      field: 'resolved',
      locale: 'resolved',
      type: 'booleanSelect',
      active: filters.active.resolved,
      value: filters.values.resolved,
      onChange: onChangeReactFilter('resolved'),
      onToggle: onToggleFilter('resolved')
    },
    {
      field: 'dateOpened',
      locale: 'date_opened',
      type: 'dateRangePicker',
      active: filters.active.dateOpened,
      values: filters.values.dateOpened,
      onChange: onChangeDateFilter('dateOpened'),
      onToggle: onToggleFilter('dateOpened')
    },
    {
      field: 'assets',
      locale: 'asset',
      type: FilterTypes.TreeMultiSelect,
      active: filters.active.assets,
      value: filters.values.assets,
      optionsForSelect: assetsForSelect,
      onChange: handleMultiSelectFilterChange('assets'),
      onToggle: onToggleFilter('assets')
    },
    {
      field: 'resolutionStatus',
      locale: 'resolution_status',
      type: FilterTypes.GenericMultiSelect,
      active: filters.active.resolutionStatus,
      optionsForSelect: resolutionStatusesForSelect.map((opt) => ({ title: opt.label, value: opt.value })),
      value: filters.values.resolutionStatus,
      onChange: handleMultiSelectFilterChange('resolutionStatus'),
      onToggle: onToggleFilter('resolutionStatus')
    },
    {
      field: 'deficiencyStatus',
      locale: 'deficiency_status',
      type: 'commonSelect',
      active: filters.active.deficiencyStatus,
      optionsForSelect: deficiencyStatusesForSelect,
      value: filters.values.deficiencyStatus,
      onChange: onChangeReactFilter('deficiencyStatus'),
      onToggle: onToggleFilter('deficiencyStatus')
    },
    {
      field: 'technician',
      backendField: 'technician',
      locale: 'reported_by',
      type: 'multiSelectWithAvatar',
      active: filters.active.technician,
      value: filters.values.technician,
      withUnassigned: true,
      onlyFullTech: true,
      itemHeight: 48,
      onChange: onChangeSearchFilter('technician'),
      onToggle: onToggleFilter('technician')
    },
    {
      field: screen,
      type: 'hidden',
      active: true,
      value: filters.values[screen]
    }
  ];

  const accountFilterList = () => {
    const list = [
      {
        field: 'resolved',
        locale: 'resolved',
        type: 'booleanSelect',
        active: filters.active.resolved,
        value: filters.values.resolved,
        onChange: onChangeReactFilter('resolved'),
        onToggle: onToggleFilter('resolved')
      },
      {
        field: 'dateOpened',
        locale: 'date_opened',
        type: 'dateRangePicker',
        active: filters.active.dateOpened,
        values: filters.values.dateOpened,
        onChange: onChangeDateFilter('dateOpened'),
        onToggle: onToggleFilter('dateOpened')
      },
      {
        field: 'building',
        locale: 'building',
        type: FilterTypes.GenericMultiSelect,
        active: filters.active.building,
        value: filters.values.building,
        onChange: handleMultiSelectFilterChange('building'),
        onToggle: onToggleFilter('building')
      },
      {
        field: screen,
        type: 'hidden',
        active: true,
        value: filters.values[screen]
      },
      {
        field: 'assets',
        locale: 'asset',
        type: FilterTypes.TreeMultiSelect,
        active: filters.active.assets,
        value: filters.values.assets,
        optionsForSelect: assetsForSelect,
        onChange: handleMultiSelectFilterChange('assets'),
        onToggle: onToggleFilter('assets')
      },
      {
        field: 'resolutionStatus',
        locale: 'resolution_status',
        type: FilterTypes.GenericMultiSelect,
        active: filters.active.resolutionStatus,
        optionsForSelect: resolutionStatusesForSelect.map((opt) => ({ title: opt.label, value: opt.value })),
        value: filters.values.resolutionStatus,
        onChange: handleMultiSelectFilterChange('resolutionStatus'),
        onToggle: onToggleFilter('resolutionStatus')
      },
      {
        field: 'deficiencyStatus',
        locale: 'deficiency_status',
        type: 'commonSelect',
        active: filters.active.deficiencyStatus,
        optionsForSelect: deficiencyStatusesForSelect,
        value: filters.values.deficiencyStatus,
        onChange: onChangeReactFilter('deficiencyStatus'),
        onToggle: onToggleFilter('deficiencyStatus')
      },
      {
        field: 'technician',
        backendField: 'technician',
        locale: 'reported_by',
        type: 'multiSelectWithAvatar',
        active: filters.active.technician,
        value: filters.values.technician,
        withUnassigned: true,
        onlyFullTech: true,
        itemHeight: 48,
        onChange: onChangeSearchFilter('technician'),
        onToggle: onToggleFilter('technician')
      },
      {
        field: 'salesPerson',
        locale: 'sales_person',
        type: FilterTypes.GenericMultiSelect,
        active: filters.active.salesPerson,
        optionsForSelect: salesPeopleForSelect.map((opt) => ({
          title: opt.subtitle,
          value: opt.value,
          subtitle: opt.label
        })),
        value: filters.values.salesPerson,
        onChange: handleMultiSelectFilterChange('salesPerson'),
        onToggle: onToggleFilter('salesPerson')
      }
    ];

    if (!globalTerritoriesApplied) {
      list.push({
        field: 'territory',
        locale: 'territory',
        type: FilterTypes.GenericMultiSelect,
        active: filters.active.territory,
        value: filters.values.territory,
        withUnassigned: true,
        onChange: handleMultiSelectFilterChange('territory'),
        onToggle: onToggleFilter('territory')
      });
    }

    list.push({
      field: 'additionalTerritory',
      backendField: 'additional_territory',
      locale: 'additional_territory',
      type: hasPrimaryTerritoryScope ? FilterTypes.GenericMultiSelect : FilterTypes.Hidden,
      active: filters.active.additionalTerritory,
      value: filters.values.additionalTerritory,
      onChange: handleMultiSelectFilterChange('additionalTerritory'),
      onToggle: onToggleFilter('additionalTerritory')
    });

    return list;
  };

  return useMemo(() => {
    if (screen === 'inspection' || screen === 'building' || screen === 'workOrder') {
      return inspectionFilterList();
    }

    if (screen === 'account') {
      return accountFilterList();
    }

    return indexFilterList();
  }, [filters, globalTerritoriesApplied]);
}
